/**
 * Der Vertrag, den jedes System einhält.
 *
 * Ein System ist eine Funktion `(welt, ctx) => void` mit **einer** Zuständigkeit,
 * die im Kopfkommentar der Datei steht. Der Orchestrator (`../schritt.ts`) führt
 * sie in einer festgeschriebenen Reihenfolge aus.
 *
 * Warum: Eine neue Mechanik ist damit eine neue Datei plus eine Zeile in der
 * Reihenfolge — kein Eingriff in bestehenden Code. Das ist die Antwort auf
 * "viele kleine Änderungen sollen keinen Spaghetti-Code provozieren"
 * (docs/2026-07-26-ueberarbeitung.md, A3).
 *
 * Regeln für jedes System:
 *   - Es liest und schreibt NUR den Weltzustand und den Kontext.
 *   - Es zieht Zufall ausschließlich aus benannten Strömen.
 *   - Es meldet Sichtbares über `effekte`, Berichtenswertes über `ereignisse`.
 *   - Es kennt kein DOM, keine Zeit, keine Netzwerkverbindung.
 */

import type { Zufall } from '@pg/rng';
import type { Absicht, EffektSicht, Ereignis } from '@pg/protocol';
import type { Inhalte } from '@pg/content';
import type { Welt } from '../typen.js';

export interface Ctx {
  zufall: Zufall;
  inhalte: Inhalte;
  /** Absichten dieses Takts — von Hand oder aus der Automatik, gleicher Typ. */
  absichten: readonly Absicht[];
  ereignisse: Ereignis[];
  effekte: EffektSicht[];
}

export type System = (welt: Welt, ctx: Ctx) => void;

/** Ein System mit Namen — der Name erscheint in Fehlermeldungen und Profilen. */
export interface BenanntesSystem {
  name: string;
  lauf: System;
}

export function system(name: string, lauf: System): BenanntesSystem {
  return { name, lauf };
}
